import { alpha, darken, outlinedInputClasses } from "@mui/material";

const primaryColor = "#0F766E";
const secondaryColor = "#F59E0B";
const errorColor = "#DC2626";
const warningColor = "#D97706";
const infoColor = "#2563EB";
const successColor = "#16A34A";

const opacityFocus = 0.22;
const opacityHover = 0.06;

const propertyInObj = (color, obj) => {
  const key = color;
  if (obj) {
    return color in obj ? String(obj[key]) : "";
  }
  return "";
};

const fontConstant = ["inter", "sans-serif"].join(",");
export const typographyProperties = {
  htmlFontSize: 16,
  fontFamily: fontConstant,
  fontSize: 14,
  fontWeightLight: 300,
  fontWeightRegular: 400,
  fontWeightMedium: 500,
  fontWeightBold: 700,
  h1: {
    fontFamily: fontConstant,
    fontWeight: 900,
    fontSize: "96px",
    lineHeight: "112px",
    letterSpacing: "-1.5px",
  },
  h2: {
    fontFamily: fontConstant,
    fontWeight: 900,
    fontSize: "60px",
    lineHeight: "72px",
    letterSpacing: "-0.5px",
  },
  h3: {
    fontFamily: fontConstant,
    fontWeight: 900,
    fontSize: "48px",
    lineHeight: "56.02px",
    letterSpacing: "-1.5px",
  },
  h4: {
    fontFamily: fontConstant,
    fontWeight: 600,
    fontSize: "30px",
    lineHeight: "40px",
    letterSpacing: "-1.5px",
  },
  h5: {
    fontFamily: fontConstant,
    fontWeight: 700,
    fontSize: "24px",
    lineHeight: "30px",
    letterSpacing: "-0.75px",
  },
  h6: {
    fontFamily: fontConstant,
    fontWeight: 700,
    fontSize: "20px",
    lineHeight: "24px",
    letterSpacing: "-0.5px",
  },
  subtitle1: {
    fontFamily: fontConstant,
    fontWeight: 600,
    fontSize: "16px",
    lineHeight: "22px",
    letterSpacing: "-0.3px",
  },
  subtitle2: {
    fontFamily: fontConstant,
    fontWeight: 600,
    fontSize: "15px",
    lineHeight: "18px",
    letterSpacing: "-0.35px",
  },
  body1: {
    fontFamily: fontConstant,
    fontWeight: 400,
    fontSize: "15px",
    lineHeight: "24px",
    letterSpacing: "-0.3px",
  },
  body2: {
    fontFamily: fontConstant,
    fontWeight: 400,
    fontSize: "14px",
    lineHeight: "22px",
    letterSpacing: "-0.2px",
  },
  button: {
    fontFamily: fontConstant,
    fontWeight: 600,
    fontSize: "13px",
    lineHeight: "20px",
    letterSpacing: "-0.25px",
  },
  caption: {
    fontFamily: fontConstant,
    fontWeight: 500,
    fontSize: "12px",
    lineHeight: "16px",
    letterSpacing: "-0.15px",
  },
  overline: {
    fontFamily: fontConstant,
    fontWeight: 600,
    fontSize: "10px",
    lineHeight: "12px",
    letterSpacing: "1.25px",
    textTransform: "uppercase",
  },
  info: {
    fontFamily: fontConstant,
    fontWeight: 400,
    fontSize: "13px",
    lineHeight: "20px",
    letterSpacing: "-0.1px",
  },
};

export const lightModePalette = {
  mode: "light",
  common: {
    black: "#000000",
    white: "#FFFFFF",
  },
  background: {
    paper: "#FFFFFF",
    default: "#F7FAFC",
  },
  text: {
    primary: "#102A43",
    secondary: "#52606D",
    tertiary: "#829AB1",
  },
  primary: {
    main: primaryColor,
    dark: darken(primaryColor, opacityFocus),
    light: alpha(primaryColor, opacityHover),
    contrastText: "#FFFFFF",
  },
  secondary: {
    main: secondaryColor,
    dark: darken(secondaryColor, opacityFocus),
    light: alpha(secondaryColor, opacityHover),
    contrastText: "#111827",
  },
  error: {
    main: errorColor,
    dark: darken(errorColor, opacityFocus),
    light: alpha(errorColor, opacityHover),
    contrastText: "#FFFFFF",
  },
  warning: {
    main: warningColor,
    dark: darken(warningColor, opacityFocus),
    light: alpha(warningColor, opacityHover),
    contrastText: "#111827",
  },
  info: {
    main: infoColor,
    dark: darken(infoColor, opacityFocus),
    light: alpha(infoColor, opacityHover),
    contrastText: "#FFFFFF",
  },
  success: {
    main: successColor,
    dark: darken(successColor, opacityFocus),
    light: alpha(successColor, opacityHover),
    contrastText: "#FFFFFF",
  },
  grey: {
    50: "#F0F4F8",
    100: "#D9E2EC",
    200: "#BCCCDC",
    300: "#9FB3C8",
    400: "#829AB1",
    500: "#627D98",
    600: "#486581",
    700: "#334E68",
    800: "#243B53",
    900: "#102A43",
  },
  inheritWhite: {
    main: "#ffffff",
  },
  divider: "#102A431A",
  other: {
    outlinedEnabledBorder: "#334E68",
    outlinedBorderDarker: "rgba(130, 154, 177, 0.28)",
    dividerTwo: "#102A430F",
    darkSecondary: "#D97706",
    lightSecondary: "#F59E0B40",
  },
};

export const ElementProps = {
  MuiButton: {
    defaultProps: {
      variant: "contained",
      disableElevation: true,
    },
    variants: [
      {
        props: { variant: "tonal" },
        style: {},
      },
      {
        props: { variant: "noPadding" },
        style: {
          padding: 0,
        },
      },
    ],
    styleOverrides: {
      root: {
        minWidth: "max-content",
        borderRadius: "8px",
        textTransform: "capitalize",
        fontWeight: 600,
        gap: 8,
      },
      icon: {
        margin: 0,
      },
      startIcon: {
        margin: 0,
      },
      endIcon: {
        margin: 0,
      },
      sizeMedium: {
        fontSize: 13,
        lineHeight: "20px",
        letterSpacing: "-0.25px",
        padding: "8px 12px",
      },
      sizeSmall: {
        fontSize: 12,
        lineHeight: "16px",
        letterSpacing: "-0.2px",
        padding: "4px 8px",
      },
      text: {
        boxShadow: "none",
      },
      containedInherit: {
        backgroundColor: lightModePalette.text?.primary,
        color: propertyInObj("main", lightModePalette.inheritWhite),
        "&:hover": {
          backgroundColor: lightModePalette.text?.primary,
        },
      },
      outlinedInherit: {
        borderColor: lightModePalette.grey?.["50"],
      },
      outlinedInheritWhite: {
        backgroundColor: lightModePalette.text?.primary,
        borderColor: propertyInObj("main", lightModePalette.inheritWhite),
        "&:hover": {
          backgroundColor: lightModePalette.text?.primary,
        },
      },
      tonalPrimary: {
        background: propertyInObj("light", lightModePalette.primary),
        color: propertyInObj("main", lightModePalette.primary),
        "&:hover": {
          backgroundColor: alpha(propertyInObj("main", lightModePalette.primary), opacityHover),
        },
      },
      tonalSecondary: {
        background: propertyInObj("light", lightModePalette.secondary),
        color: propertyInObj("main", lightModePalette.secondary),
        "&:hover": {
          backgroundColor: alpha(propertyInObj("main", lightModePalette.secondary), opacityHover),
        },
      },
      tonalError: {
        background: propertyInObj("light", lightModePalette.error),
        color: propertyInObj("main", lightModePalette.error),
        "&:hover": {
          backgroundColor: alpha(propertyInObj("main", lightModePalette.error), opacityHover),
        },
      },
      tonalWarning: {
        background: propertyInObj("light", lightModePalette.warning),
        color: propertyInObj("main", lightModePalette.warning),
        "&:hover": {
          backgroundColor: alpha(propertyInObj("main", lightModePalette.warning), opacityHover),
        },
      },
      tonalInfo: {
        background: propertyInObj("light", lightModePalette.info),
        color: propertyInObj("main", lightModePalette.info),
        "&:hover": {
          backgroundColor: alpha(propertyInObj("main", lightModePalette.info), opacityHover),
        },
      },
      tonalSuccess: {
        background: propertyInObj("light", lightModePalette.success),
        color: propertyInObj("main", lightModePalette.success),
        "&:hover": {
          backgroundColor: alpha(propertyInObj("main", lightModePalette.success), opacityHover),
        },
      },
      tonalInherit: {
        backgroundColor: alpha(propertyInObj("primary", lightModePalette.text), opacityHover),
        color: lightModePalette.text?.primary,
        "&:hover": {
          backgroundColor: alpha(propertyInObj("primary", lightModePalette.text), opacityHover),
        },
      },
    },
  },
  MuiChip: {
    variants: [
      {
        props: { size: "xSmall" },
        style: {
          height: 24,
          fontSize: 12,
          lineHeight: "16px",
          paddingInline: "4px",
          "& .MuiChip-icon": {
            fontSize: 14,
          },
          "& .MuiChip-deleteIcon": {
            fontSize: 14,
          },
          "& .MuiChip-label": {
            paddingInline: 8,
          },
        },
      },
      {
        props: { variant: "tonal" },
        style: { fontWeight: 600 },
      },
    ],

    styleOverrides: {
      root: {
        "&:active": {
          boxShadow: "unset",
        },
        fontWeight: 400,
      },

      // Tonal
      tonalDefault: {
        backgroundColor: lightModePalette.grey?.["50"],
        color: lightModePalette.text?.secondary,
      },
      tonalPrimary: {
        backgroundColor: alpha(propertyInObj("main", lightModePalette.primary), 0.08),
        color: propertyInObj("main", lightModePalette.primary),
      },
      tonalSecondary: {
        backgroundColor: alpha(propertyInObj("main", lightModePalette.secondary), 0.08),
        color: propertyInObj("main", lightModePalette.secondary),
      },
      tonalError: {
        backgroundColor: alpha(propertyInObj("main", lightModePalette.error), 0.08),
        color: propertyInObj("main", lightModePalette.error),
      },
      tonalWarning: {
        backgroundColor: alpha(propertyInObj("main", lightModePalette.warning), 0.08),
        color: propertyInObj("main", lightModePalette.warning),
      },
      tonalInfo: {
        backgroundColor: alpha(propertyInObj("main", lightModePalette.info), 0.08),
        color: propertyInObj("main", lightModePalette.info),
      },
      tonalSuccess: {
        backgroundColor: alpha(propertyInObj("main", lightModePalette.success), 0.08),
        color: propertyInObj("main", lightModePalette.success),
      },

      // Tonal Delete Icon
      deleteIconTonalColorPrimary: {
        fill: propertyInObj("dark", lightModePalette.primary),
      },
      deleteIconTonalColorSecondary: {
        fill: propertyInObj("dark", lightModePalette.secondary),
      },
      deleteIconTonalColorError: {
        fill: propertyInObj("dark", lightModePalette.error),
      },
      deleteIconTonalColorWarning: {
        fill: propertyInObj("dark", lightModePalette.warning),
      },
      deleteIconTonalColorInfo: {
        fill: propertyInObj("dark", lightModePalette.info),
      },
      deleteIconTonalColorSuccess: {
        fill: propertyInObj("dark", lightModePalette.success),
      },
    },
  },
  MuiInputAdornment: {
    styleOverrides: {
      sizeSmall: {
        positionStart: {
          marginRight: "-2px",
        },
        positionEnd: {
          marginInlineStart: 0,
          marginInlineEnd: 8,
        },
      },
      positionStart: {
        // marginRight: '-4px',
        "& svg": { margin: "8px -4px 8px 12px" },
        "& p": {
          fontSize: 14,
          height: "unset",
          padding: "8px 0 8px 12px",
        },
        "&.MuiInputAdornment-sizeSmall": {
          "& p": { padding: "6px 10px" },
        },
      },
      positionEnd: {
        marginInlineStart: 0,
        marginInlineEnd: 8,
        // '& svg': { margin: '8px 8px 8px -8px' },
        "& p": {
          fontSize: 14,
          height: "unset",
          padding: "8px 10px",
        },
        "&.MuiInputAdornment-sizeSmall": {
          "& p": { padding: "6px 10px" },
        },
      },
    },
  },
  MuiInputLabel: {
    defaultProps: {
      shrink: true,
      disableAnimation: true,
      size: "small",
    },
    styleOverrides: {
      root: {
        position: "relative",
        transform: "unset",
        ...typographyProperties.body1,
        fontWeight: 600,
        color: lightModePalette.text?.primary,
        "&.Mui-error": {
          "&.Mui-focused": {
            color: propertyInObj("main", lightModePalette.error),
          },
        },
      },
      sizeSmall: {
        fontSize: "13px",
        lineHeight: "28px",
        letterSpacing: "-0.15px",
      },
    },
  },
  MuiFormControl: {
    styleOverrides: {
      root: {
        "&.MuiTextField-root label.Mui-focused": {
          color: "initial",
        },
      },
    },
  },
  MuiFormLabel: {
    styleOverrides: {
      root: {
        "&.Mui-focused": {
          color: lightModePalette.text?.primary,
        },
      },
    },
  },
  MuiTextField: {
    defaultProps: {
      hiddenLabel: true,
    },
    styleOverrides: {
      root: {
        borderRadius: "10px",
        "& label.Mui-focused": {
          color: lightModePalette.text?.secondary,
        },
        input: {
          fontSize: "16px",
          padding: "12px",
          '&[type="search"]::-webkit-search-cancel-button': {
            marginInlineStart: "8px",
          },
        },
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        color: propertyInObj("primary", lightModePalette.text),
        borderColor: lightModePalette.grey?.["100"],
        backgroundColor: lightModePalette.grey?.["50"],
        borderRadius: "8px",
        [`&:hover .${outlinedInputClasses.notchedOutline}`]: {
          borderColor: lightModePalette.grey?.["200"],
        },
        [`&.Mui-focused .${outlinedInputClasses.notchedOutline}`]: {
          borderWidth: 1.5,
          borderColor: propertyInObj("primary", lightModePalette.text),
        },
        [`&.Mui-disabled .${outlinedInputClasses.notchedOutline}`]: {
          border: "none",
        },
        "& legend": {
          display: "none",
        },
        "&.Mui-error": {
          backgroundColor: alpha(propertyInObj("main", lightModePalette.error), 0.05),
          input: {
            color: propertyInObj("main", lightModePalette.error),
          },
          [`&:hover .${outlinedInputClasses.notchedOutline}`]: {
            borderColor: propertyInObj("main", lightModePalette.error),
          },
          [`&.Mui-focused .${outlinedInputClasses.notchedOutline}`]: {
            borderColor: propertyInObj("main", lightModePalette.error),
          },
        },
      },
      input: {
        fontSize: "16px",
        fontWeight: 400,
        lineHeight: "20px",
        letterSpacing: "-0.25px",
        padding: "12px",
      },
      inputMultiline: {
        padding: 0,
      },
      notchedOutline: {
        top: 0,
        borderWidth: 1.5,
        borderColor: lightModePalette.grey?.["100"],
      },
      adornedStart: {
        paddingLeft: 0,
      },
      adornedEnd: {
        paddingRight: 0,
      },
      multiline: {
        padding: "12px",
        [`&.${outlinedInputClasses.sizeSmall}`]: {
          padding: "8px 12px",
        },
      },
      sizeSmall: {
        input: {
          padding: "8px 12px",
          '&[type="search"]::-webkit-search-cancel-button': {
            marginInlineStart: "8px",
          },
        },
      },
    },
  },
  MuiSelect: {
    styleOverrides: {
      select: {
        "&.MuiInputBase-inputSizeSmall": {
          padding: "8px 12px",
          paddingRight: "32px",
        },
      },
    },
  },
  MuiFilledInput: {
    styleOverrides: {
      root: {
        background: `${alpha("#fff", 0.1)} !important`,
        borderRadius: 8,
        overflow: "hidden",
        // border:'2px solid transparent',
        "&:hover": {
          background: alpha(`${lightModePalette?.common?.white}`, 0.1),
        },
        "&.Mui-error": {
          backgroundColor: alpha(propertyInObj("main", lightModePalette.error), 0.05),
          input: {
            color: propertyInObj("main", lightModePalette.error),
          },
        },
      },
      input: {
        fontSize: "16px",
        fontWeight: 400,
        lineHeight: "20px",
        letterSpacing: "-0.25px",
        padding: "12px",
        borderRadius: 8,
      },
      inputMultiline: {
        padding: 0,
      },
      adornedStart: {
        paddingLeft: 0,
      },
      adornedEnd: {
        paddingRight: 0,
      },
      multiline: {
        padding: "12px",
        [`&.${outlinedInputClasses.sizeSmall}`]: {
          padding: "8px 12px",
        },
      },
      sizeSmall: {
        input: {
          padding: "8px 12px",
          '&[type="search"]::-webkit-search-cancel-button': {
            marginInlineStart: "8px",
          },
        },
      },
    },
  },
  MuiPaper: {
    defaultProps: {
      elevation: 0,
    },
    styleOverrides: {
      root: {
        borderRadius: "12px",
      },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      root: {
        padding: "8px 12px",
        borderColor: lightModePalette.grey?.["50"],
        fontWeight: 400,
        fontSize: "14px",
        lineHeight: "22px",
        letterSpacing: "-0.2px",
        "&:not(first-of-type)": {
          color: lightModePalette.text?.secondary,
        },
        "&.MuiTableCell-head": {
          padding: "8px 12px",
          backgroundColor: "#fff",
          color: lightModePalette.text?.tertiary,
          fontWeight: 600,
          fontSize: "12px",
          lineHeight: "20px",
          letterSpacing: "-0.1px",
          borderColor: lightModePalette.grey?.["100"],
        },
      },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: {
        textTransform: "capitalize",
      },
    },
  },
  MuiList: {
    styleOverrides: {
      root: {
        paddingTop: 0,
        paddingBottom: 0,
      },
    },
  },
  MuiListItemButton: {
    styleOverrides: {
      root: {
        borderRadius: 12,
        gap: 8,
      },
    },
  },
  MuiListItemIcon: {
    styleOverrides: {
      root: {
        minWidth: "unset",
        marginRight: 0,
      },
    },
  },
  MuiStepButton: {
    styleOverrides: {
      root: {
        padding: 8,
        margin: 0,
      },
    },
  },
  MuiStepLabel: {
    styleOverrides: {
      label: {
        fontSize: 13,
        fontWeight: 400,
        lineHeight: "20px",
        letterSpacing: "-0.1px",
        "&.Mui-active": {
          fontWeight: 400,
        },
      },
      iconContainer: {
        "& .MuiStepIcon-root.Mui-completed": {
          color: lightModePalette?.common?.black,
        },
      },
    },
  },
  MuiFormHelperText: {
    styleOverrides: {
      root: {
        margin: "2px 8px 0 0",
        fontSize: "12px",
        fontWeight: 400,
        color: lightModePalette.text?.tertiary,
        "&.Mui-error": {
          color: propertyInObj("main", lightModePalette.primary),
        },
      },
    },
  },
  MuiPopover: {
    styleOverrides: {
      paper: {
        borderRadius: "8px",
        boxShadow: `0px 20px 31px 3px #0E0C0C1A`,
        border: `1px solid #0E0C0C0D`,
      },
    },
  },
  MuiMenuItem: {
    styleOverrides: {
      root: {
        gap: 8,
      },
    },
  },
  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        backgroundColor: lightModePalette?.common?.black,
      },
      arrow: {
        color: lightModePalette?.common?.black,
      },
    },
  },
  MuiAlert: {
    styleOverrides: {
      root: {
        alignItems: "center",
        gap: 8,
      },
      action: {
        padding: 0,
      },
      icon: {
        margin: 0,
      },
    },
  },
  MuiCheckbox: {
    defaultProps: {
      color: "primary",
      // icon: <CheckboxUnchecked />,
      // checkedIcon: <CheckboxCheckedIcon />,
      // indeterminateIcon: <IndeterminateIcon />,
    },
    variants: [
      {
        props: { size: "large" },
        style: {
          svg: {
            width: "28px",
            height: "28px",
          },
        },
      },
      {
        props: { size: "medium" },
        style: {
          svg: {
            width: "24px",
            height: "24px",
          },
        },
      },
      {
        props: { size: "small" },
        style: {
          svg: {
            width: "16px",
            height: "16px",
          },
        },
      },
    ],
    styleOverrides: {
      root: {
        "& .MuiSvgIcon-fontSizeLarge": {
          fontSize: "28px",
        },
        "&.Mui-disabled": {
          "& svg path": {
            fill: propertyInObj("disabled", lightModePalette.action),
          },
        },
      },
      colorPrimary: {
        "&.Mui-checked": {
          "& svg path": {
            fill: propertyInObj("main", lightModePalette.primary),
          },
        },
      },
      colorSecondary: {
        "&.Mui-checked": {
          "& svg path": {
            fill: propertyInObj("main", lightModePalette.secondary),
          },
        },
      },
      colorError: {
        "&.Mui-checked": {
          "& svg path": {
            fill: propertyInObj("main", lightModePalette.error),
          },
        },
      },
      colorInfo: {
        "&.Mui-checked": {
          "& svg path": {
            fill: propertyInObj("main", lightModePalette.info),
          },
        },
      },
      colorWarning: {
        "&.Mui-checked": {
          "& svg path": {
            fill: propertyInObj("main", lightModePalette.warning),
          },
        },
      },
      colorSuccess: {
        "&.Mui-checked": {
          "& svg path": {
            fill: propertyInObj("main", lightModePalette.success),
          },
        },
      },
    },
  },
};
